import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import { obterArquivoAnexo } from "../../../utils/solicitacoes/anexoCache";

const EXTENSOES_IMAGEM = ["png", "jpg", "jpeg", "gif", "webp"];
const EXTENSOES_VIDEO = ["mp4", "webm", "ogg", "mov"];
const EXTENSOES_VISUALIZAVEIS = [...EXTENSOES_IMAGEM, ...EXTENSOES_VIDEO, "pdf"];

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_PASSO = 0.25;

function extensao(nome = "") {
    return nome.split(".").pop()?.toLowerCase();
}

export function ehVisualizavel(nome) {
    return EXTENSOES_VISUALIZAVEIS.includes(extensao(nome));
}

function limitarZoom(valor) {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(valor * 100) / 100));
}

// Busca o binário do anexo e monta uma URL local (blob) só para exibir dentro do sistema,
// sem forçar o download como o botão "Baixar" faz.
export default function AnexoVisualizarModal({ anexo, onClose }) {
    const [url, setUrl] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const arrastandoRef = useRef(false);
    const ultimaPosRef = useRef({ x: 0, y: 0 });

    // A URL vem do cache compartilhado (anexoCache) e não é revogada ao fechar — reabrir o mesmo anexo é instantâneo.
    useEffect(() => {
        if (!anexo) return undefined;

        let cancelado = false;
        setUrl(null);
        setCarregando(true);
        setZoom(1);
        setPos({ x: 0, y: 0 });

        obterArquivoAnexo(anexo)
            .then(({ url: urlArquivo }) => {
                if (!cancelado) setUrl(urlArquivo);
            })
            .catch(error => {
                console.error("Erro ao carregar anexo para visualização", error);
                toast.error("Erro ao carregar anexo para visualização");
            })
            .finally(() => {
                if (!cancelado) setCarregando(false);
            });

        return () => {
            cancelado = true;
        };
    }, [anexo]);

    function aplicarZoom(delta) {
        setZoom(z => {
            const novo = limitarZoom(z + delta);
            if (novo <= 1) setPos({ x: 0, y: 0 });
            return novo;
        });
    }

    function resetarZoom() {
        setZoom(1);
        setPos({ x: 0, y: 0 });
    }

    function handleWheel(e) {
        aplicarZoom(e.deltaY > 0 ? -ZOOM_PASSO : ZOOM_PASSO);
    }

    function handlePointerDown(e) {
        if (zoom <= 1) return;
        arrastandoRef.current = true;
        ultimaPosRef.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
    }

    function handlePointerMove(e) {
        if (!arrastandoRef.current) return;
        const dx = e.clientX - ultimaPosRef.current.x;
        const dy = e.clientY - ultimaPosRef.current.y;
        ultimaPosRef.current = { x: e.clientX, y: e.clientY };
        setPos(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    }

    function handlePointerUp() {
        arrastandoRef.current = false;
    }

    function handleDoubleClick() {
        if (zoom > 1) resetarZoom();
        else aplicarZoom(1);
    }

    if (!anexo) return null;

    const ehImagem = EXTENSOES_IMAGEM.includes(extensao(anexo.nomeOriginal));
    const ehVideo = EXTENSOES_VIDEO.includes(extensao(anexo.nomeOriginal));

    return (
        <Dialog
            contentClassName="p-0"
            blockScroll
            visible={!!anexo}
            onHide={onClose}
            header={anexo.nomeOriginal}
            style={{ width: "90%" }}
        >
            {!carregando && url && ehImagem && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-card">
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            title="Diminuir zoom"
                            onClick={() => aplicarZoom(-ZOOM_PASSO)}
                            disabled={zoom <= ZOOM_MIN}
                            className="p-2 rounded-full text-primary hover:bg-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                            <i className="fa fa-search-minus" />
                        </button>
                        <span className="text-xs font-medium text-muted-foreground w-12 text-center select-none">
                            {Math.round(zoom * 100)}%
                        </span>
                        <button
                            type="button"
                            title="Aumentar zoom"
                            onClick={() => aplicarZoom(ZOOM_PASSO)}
                            disabled={zoom >= ZOOM_MAX}
                            className="p-2 rounded-full text-primary hover:bg-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                            <i className="fa fa-search-plus" />
                        </button>
                    </div>
                    <button
                        type="button"
                        title="Restaurar zoom"
                        onClick={resetarZoom}
                        disabled={zoom === 1 && pos.x === 0 && pos.y === 0}
                        className="p-2 rounded-full text-primary hover:bg-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                        <i className="fa fa-compress" />
                    </button>
                </div>
            )}

            <div className="flex items-center justify-center bg-muted/30 rounded-b-lg">
                {carregando && <i className="fa fa-spinner fa-spin text-2xl text-muted-foreground" />}
                {!carregando && url && ehImagem && (
                    <div
                        className="w-full h-[70vh] overflow-hidden flex items-center justify-center select-none"
                        onWheel={handleWheel}
                    >
                        <img
                            src={url}
                            alt={anexo.nomeOriginal}
                            draggable={false}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerLeave={handlePointerUp}
                            onDoubleClick={handleDoubleClick}
                            style={{
                                transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
                                transition: arrastandoRef.current ? "none" : "transform 0.1s ease-out",
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                cursor: zoom > 1 ? "grab" : "zoom-in",
                            }}
                        />
                    </div>
                )}
                {!carregando && url && ehVideo && (
                    <video
                        src={url}
                        controls
                        className="w-full h-[80vh]"
                        style={{ objectFit: "contain" }}
                    />
                )}
                {!carregando && url && !ehImagem && !ehVideo && (
                    <embed src={url} type="application/pdf" className="w-full h-[80vh]" />
                )}
            </div>
        </Dialog>
    );
}
